import { Theme, useTheme } from "@/components/theme/themeProvider"
import { useChatStoreActions } from "@/modules/chat/stores/useChatStore"
import { appQueryClient } from "@/modules/core/api/queryClient"
import { resetAllStores } from "@/modules/core/store"
import { useCurrentSpaceManagerActions } from "@/modules/core/store/useCurrentSpaceManagerStore"
import { LoggerType } from "@/modules/utils/loggerUtils"
import { signOut } from "aws-amplify/auth"
import { useCallback, useEffect } from "react"

/**
 * Module-level logout core — does NOT depend on any mounted React component.
 *
 * The 401 interceptor calls logout401Function. Previously its default was an empty no-op that was
 * only replaced once a component mounted useLogout(). When a 401 fired after the React tree had
 * unmounted (white-screen crash), the no-op ran, leaving @auth/user persisted while the Cognito
 * tokens were already gone — a permanent white-screen loop across reloads. This core clears both
 * sides (Cognito session + persisted Zustand stores + query cache) regardless of mount state.
 */
export const performAuthLogout = async () => {
  try {
    await signOut()
  } catch (e) {
    console.warn(LoggerType.Error + "[Logout] signOut failed (ignoring)", e)
  }
  // resetAllStores resets every Zustand store (incl. @auth/user and chat) to initial state, which the
  // persist middleware writes back to localStorage — clearing the stale @auth/user flag. The live
  // StreamChat socket is not torn down here (no mounted component); the full useLogout handles that.
  resetAllStores()
  appQueryClient.clear()
}

export let logout401Function: () => Promise<void> = performAuthLogout

export const useLogout = () => {
  const { setTheme } = useTheme()
  const { disconnectChatUser } = useChatStoreActions()
  const { setBoxId, setCoachId } = useCurrentSpaceManagerActions()

  const logout = useCallback(async () => {
    setTheme(Theme.AthleteLight)
    await disconnectChatUser()
    await signOut()
    resetAllStores()
    setBoxId(null)
    setCoachId(null)
    appQueryClient.clear()
  }, [setTheme, disconnectChatUser, setBoxId, setCoachId])

  useEffect(() => {
    logout401Function = logout
    return () => {
      // restore module-level core when no component owns logout (prevents stale unmounted closure)
      logout401Function = performAuthLogout
    }
  }, [logout])

  return logout
}
